import React, { useRef, useState } from 'react';
import { ServiceNodeGroup } from '@bumble/app/__generated__/data-contracts';
import { Loading } from '@bumble/app/components/Loading';
import { PageLoading } from '@bumble/app/components/PageLoading';
import { ServiceNodeCreate } from '@bumble/app/components/service-node/ServiceNodeCreate';
import { useApi } from '@bumble/app/hooks/use-api';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Slide,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

interface AppServiceCreateProps {
  handleClose: () => void;
}
export const AppServiceCreate = ({ handleClose }: AppServiceCreateProps) => {
  const api = useApi();
  const containerRef = useRef(null);
  const [loadingModelOpen, setLoadingModalOpen] = useState(false);
  const [serviceName, setServiceName] = useState('');
  const [endpointServiceName, setEndpointServiceName] = useState('');
  const [azureServiceId, setAzureServiceId] = useState('');
  const [dnsName, setDnsName] = useState('');
  const [port, setPort] = useState('');
  const [realSubnets, setRealSubnets] = useState('');
  const [enableAddressTranslation, setEnableAddressTranslation] = useState(false);
  const [virtualSubnetPools, setVirtualSubnetPools] = useState('100.66.0.0/16');
  const [enableBGP, setEnableBGP] = useState(false);
  const [bgpLocalAsn, setBgpLocalAsn] = useState('');
  const [bgpPeerAsn, setBgpPeerAsn] = useState('');
  const [bgpPeerIp, setBgpPeerIp] = useState('');

  const [selectedAwsRegion, setSelectedAwsRegion] = React.useState('');
  const [selectedAzureRegion, setSelectedAzureRegion] = React.useState('');
  const [selectedServiceNodeGroup, setSelectedServiceNodeGroup] = React.useState('');

  const [showNodeCreate, setShowNodeCreate] = useState(false);
  const [location, setLocation] = useState('onprem');

  const [protocol, setProtocol] = React.useState('tcp');
  const handleProtocolChange = (event: SelectChangeEvent) => {
    setProtocol(event.target.value as string);
  };

  const [openAi, setOpenAi] = useState(false);
  const [azurePrivateIp, setAzurePrivateIp] = useState('');
  const [azureResourceId, setAzureResourceId] = useState('');
  const [azureUserEmail, setAzureUserEmail] = useState('');

  const appPost = useMutation(api.createAppServiceApiV1CreateAppServicePost, {
    onSuccess() {
      toast.success('App Service created.');
      handleClose();
    },
    onError(error: any) {
      if (Array.isArray((error as any).error.detail)) {
        (error as any).error.detail.forEach((el: any) =>
          toast.error(el.message, { autoClose: false, hideProgressBar: true }),
        );
      } else {
        toast.error((error as any).error.detail, { autoClose: false, hideProgressBar: true });
      }
      setLoadingModalOpen(false);
    },
  });

  const handleSubmit = () => {
    switch (location) {
      case 'aws':
        setLoadingModalOpen(true);
        appPost.mutate({
          app_service_name: serviceName,
          location,
          region: selectedAwsRegion,
          cloud_endpoint_service_name: endpointServiceName,
          proto: 'tcp',
          port,
        });
        break;
      case 'onprem':
        appPost.mutate({
          app_service_name: serviceName,
          location,
          fqdn: dnsName,
          service_node_group_id: selectedServiceNodeGroup,
          proto: protocol,
          port,
        });
        break;
      case 'azure':
        if (openAi) {
          appPost.mutate({
            app_service_name: serviceName,
            location,
            region: selectedAzureRegion,
            proto: protocol,
            port,
            open_ai_private_link: openAi,
            private_endpoint_ip_address: azurePrivateIp,
            azure_user_email: azureUserEmail,
            remote_vnet_resource_id: azureResourceId,
          });
        } else {
          appPost.mutate({
            app_service_name: serviceName,
            location,
            region: selectedAzureRegion,
            cloud_endpoint_service_name: azureServiceId,
            proto: protocol,
            port,
          });
        }
        break;
      case 'network':
        appPost.mutate({
          app_service_name: serviceName,
          location,
          real_subnets: realSubnets,
          network_mode_enabled: true,
          address_translation_enabled: enableAddressTranslation,
          ...(enableAddressTranslation && {
            virtual_subnet_pools: virtualSubnetPools,
          }),
          bgp_enabled: enableBGP,
          ...(enableBGP && {
            bgp_local_asn: Number(bgpLocalAsn),
            bgp_peer_asn: Number(bgpPeerAsn),
            bgp_peer_ip: bgpPeerIp,
          }),
          service_node_group_id: selectedServiceNodeGroup,
        });
        break;

      default:
        break;
    }
  };

  const isDisabled = () => {
    switch (location) {
      case 'aws':
        return !(serviceName && selectedAwsRegion && endpointServiceName && port);
      case 'onprem':
        return !(serviceName && dnsName && selectedServiceNodeGroup && protocol && port);
      case 'azure':
        if (openAi) {
          return !(
            serviceName &&
            protocol &&
            port &&
            selectedAzureRegion &&
            azurePrivateIp &&
            azureResourceId &&
            azureUserEmail
          );
        }
        if (!openAi) {
          return !(serviceName && protocol && port && selectedAzureRegion && azureServiceId);
        }
        return true;
      case 'network':
        if (enableAddressTranslation && enableBGP) {
          return !(
            serviceName &&
            realSubnets &&
            virtualSubnetPools &&
            bgpLocalAsn &&
            bgpPeerAsn &&
            bgpPeerIp &&
            selectedServiceNodeGroup
          );
        }
        if (enableAddressTranslation && !enableBGP) {
          return !(serviceName && realSubnets && virtualSubnetPools && selectedServiceNodeGroup);
        }
        if (!enableAddressTranslation && enableBGP) {
          return !(
            serviceName &&
            realSubnets &&
            bgpLocalAsn &&
            bgpPeerAsn &&
            bgpPeerIp &&
            selectedServiceNodeGroup
          );
        }
        if (!enableAddressTranslation && !enableBGP) {
          return !(serviceName && realSubnets && selectedServiceNodeGroup);
        }
        return true;
      default:
        return true;
    }
  };
  const { isLoading: awsLoading, data: awsData } = useQuery(
    ['awsRegions'],
    api.listAwsRegionsApiV1ListAwsRegionsGet.bind(null, {}),
    { enabled: true },
  );

  const { isLoading: azureLoading, data: azureData } = useQuery(
    ['azureRegions'],
    api.listAzureRegionsApiV1ListAzureRegionsGet.bind(null, {}),
    { enabled: true },
  );

  const {
    isLoading: groupIsLoading,
    data: groupData,
    refetch: groupRefetch,
  } = useQuery(
    ['serviceNodeGroups'],
    api.listServiceNodeGroupApiV1ListServiceNodeGroupGet.bind(null, {}),
    { enabled: true },
  );

  const handleCreateModalClose = () => {
    setShowNodeCreate(false);
    groupRefetch();
  };

  const handleOpenAiChange = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setOpenAi(value === 'true');
  };

  if (awsLoading || azureLoading || groupIsLoading) {
    return <Loading />;
  }
  return (
    <Paper ref={containerRef} elevation={0}>
      <Slide
        direction="right"
        in={!showNodeCreate}
        appear={false}
        container={containerRef.current}
        mountOnEnter
        unmountOnExit
      >
        <Box pt={2}>
          <Box>
            <Grid container spacing={4}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  id="app-service-name"
                  label="Service Name"
                  variant="outlined"
                  value={serviceName}
                  onChange={(e) => setServiceName(e.target.value)}
                  autoFocus
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="deployment-label">Location</InputLabel>
                  <Select
                    labelId="deployment-label"
                    id="deployment"
                    label="Location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                  >
                    <MenuItem value="onprem">On-Prem</MenuItem>
                    <MenuItem value="aws">AWS</MenuItem>
                    <MenuItem value="azure">Azure</MenuItem>
                    <MenuItem value="network">Network</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {location === 'aws' && (
              <>
                <Button
                  href="https://www.bumblebeenet.com/post/how-to-create-an-app-service-for-an-application-in-aws"
                  target="_blank"
                  rel="noreferrer"
                  variant="text"
                  size="small"
                  endIcon={<InfoOutlinedIcon color="secondary" fontSize="small" />}
                  sx={{ my: 2 }}
                >
                  Read me first
                </Button>
                <Typography variant="body2" sx={{ mb: 8 }}>
                  Make sure you approve{' '}
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    Bumblebee Networks
                  </Box>{' '}
                  to connect to this Endpoint Service by going to AWS console and adding
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    “arn:aws:iam::653209187112:role/bumblebee-api-iam”
                  </Box>{' '}
                  as an entry in the Allow Principal for the Endpoint Service. For more information,{' '}
                  <Link
                    href="https://www.bumblebeenet.com/post/how-to-grant-bumblebee-networks-to-access-your-endpoint-service"
                    target="_blank"
                    rel="noreferrer"
                  >
                    click here
                  </Link>
                  .
                </Typography>
                <Grid container spacing={4}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="App Protocol"
                      variant="outlined"
                      sx={{ mb: 4 }}
                      value="TCP"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      id="port"
                      label="Port"
                      variant="outlined"
                      sx={{ mb: 4 }}
                      value={port}
                      onChange={(e) => setPort(e.target.value)}
                    />
                  </Grid>
                </Grid>
                <FormControl fullWidth sx={{ mb: 4 }}>
                  <InputLabel id="aws-regions-label">AWS Region</InputLabel>
                  <Select
                    labelId="aws-regions-label"
                    id="aws-regions"
                    label="AWS Region"
                    value={selectedAwsRegion}
                    onChange={(e) => setSelectedAwsRegion(e.target.value)}
                  >
                    {awsData?.data.map((region: string) => (
                      <MenuItem value={region} key={region}>
                        {region}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  id="provider-endpoint-service-name"
                  label="AWS Endpoint Service Name"
                  variant="outlined"
                  sx={{ mb: 4 }}
                  value={endpointServiceName}
                  onChange={(e) => setEndpointServiceName(e.target.value)}
                />
              </>
            )}
            {location === 'onprem' && (
              <>
                <Button
                  href="https://www.bumblebeenet.com/post/how-to-create-an-app-service-for-on-prem-application"
                  target="_blank"
                  rel="noreferrer"
                  variant="text"
                  size="small"
                  endIcon={<InfoOutlinedIcon color="secondary" fontSize="small" />}
                  sx={{ my: 2 }}
                >
                  Learn more about OnPrem
                </Button>
                <Typography variant="body2" sx={{ mb: 8 }}>
                  Choosing this option allows you to control and manage the accessibility of your
                  application from external networks while keeping it hosted within your on-premises
                  infrastructure.
                </Typography>
                <Grid container spacing={4}>
                  <Grid item xs={6}>
                    <FormControl fullWidth sx={{ mb: 4 }}>
                      <InputLabel id="protocol-label">App Protocol</InputLabel>
                      <Select
                        labelId="protocol-label"
                        id="protocol"
                        label="App Protocol"
                        value={protocol}
                        onChange={handleProtocolChange}
                      >
                        <MenuItem value="tcp">TCP</MenuItem>
                        <MenuItem value="udp">UDP</MenuItem>
                        <MenuItem value="ftp">FTP-Passive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      id="port"
                      label="Port"
                      variant="outlined"
                      sx={{ mb: 4 }}
                      value={port}
                      onChange={(e) => setPort(e.target.value)}
                    />
                  </Grid>
                </Grid>
                <TextField
                  fullWidth
                  id="dns-name"
                  label="App's local FQDN name or IP address"
                  variant="outlined"
                  sx={{ mb: 4 }}
                  value={dnsName}
                  onChange={(e) => setDnsName(e.target.value)}
                />
                <Grid container spacing={4}>
                  <Grid item xs={12} md={8} display="flex" alignItems="center">
                    {groupData && groupData?.data.length > 0 ? (
                      <FormControl fullWidth sx={{ mb: 4 }}>
                        <InputLabel id="service-nodes-label">Service Node Groups</InputLabel>
                        <Select
                          labelId="service-nodes-label"
                          id="service-nodes"
                          label="Service Node Groups"
                          value={selectedServiceNodeGroup}
                          onChange={(e) => setSelectedServiceNodeGroup(e.target.value)}
                        >
                          {groupData?.data.map(
                            ({
                              service_node_group_name,
                              service_node_group_id,
                            }: ServiceNodeGroup) => (
                              <MenuItem value={service_node_group_id} key={service_node_group_id}>
                                {service_node_group_name}
                              </MenuItem>
                            ),
                          )}
                        </Select>
                      </FormControl>
                    ) : (
                      <Box sx={{ textAlign: 'center', width: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                          No Service Node Groups found.
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Button
                      variant="text"
                      onClick={() => setShowNodeCreate(true)}
                      startIcon={<AddCircleOutlineOutlinedIcon fontSize="small" />}
                    >
                      New Group
                    </Button>
                  </Grid>
                </Grid>
              </>
            )}
            {location === 'azure' && (
              <>
                <Button
                  href="https://www.bumblebeenet.com/post/how-to-create-an-app-service-for-an-application-in-azure"
                  target="_blank"
                  rel="noreferrer"
                  variant="text"
                  size="small"
                  endIcon={<InfoOutlinedIcon color="secondary" fontSize="small" />}
                  sx={{ mt: 2, mb: 8 }}
                >
                  Read me first
                </Button>
                <Grid container spacing={4}>
                  <Grid item xs={6}>
                    <FormControl fullWidth sx={{ mb: 4 }}>
                      <InputLabel id="protocol-label">App Protocol</InputLabel>
                      <Select
                        labelId="protocol-label"
                        id="protocol"
                        label="App Protocol"
                        value={protocol}
                        onChange={handleProtocolChange}
                      >
                        <MenuItem value="tcp">TCP</MenuItem>
                        <MenuItem value="udp">UDP</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      id="port"
                      label="Port"
                      variant="outlined"
                      sx={{ mb: 4 }}
                      value={port}
                      onChange={(e) => setPort(e.target.value)}
                    />
                  </Grid>
                </Grid>
                <FormControl fullWidth sx={{ mb: 4 }}>
                  <InputLabel id="azure-regions-label">Azure Region</InputLabel>
                  <Select
                    labelId="azure-regions-label"
                    id="azure-regions"
                    label="Azure Region"
                    value={selectedAzureRegion}
                    onChange={(e) => setSelectedAzureRegion(e.target.value)}
                  >
                    {azureData?.data.map((region: string) => (
                      <MenuItem value={region} key={region}>
                        {region}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={{ mb: 4 }}>
                  <FormLabel id="azure-openai-label">OpenAI or Azure PaaS</FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="azure-openai-label"
                    name="azure-openai"
                    onChange={handleOpenAiChange}
                    value={openAi.toString()}
                  >
                    <FormControlLabel value="false" control={<Radio />} label="No" />
                    <FormControlLabel value="true" control={<Radio />} label="Yes" />
                  </RadioGroup>
                </FormControl>
                <Collapse in={openAi} timeout="auto">
                  <TextField
                    fullWidth
                    id="private-endpoint-private-ip"
                    label="Private Endpoint IP Address"
                    variant="outlined"
                    sx={{ mb: 4 }}
                    value={azurePrivateIp}
                    onChange={(e) => setAzurePrivateIp(e.target.value)}
                  />
                  <TextField
                    fullWidth
                    id="remote-vnet-resource-id"
                    label="Remote VNET Resource ID"
                    variant="outlined"
                    sx={{ mb: 4 }}
                    value={azureResourceId}
                    onChange={(e) => setAzureResourceId(e.target.value)}
                  />
                  <TextField
                    fullWidth
                    id="azure-user-email"
                    label="Customer User Email"
                    variant="outlined"
                    sx={{ mb: 4 }}
                    value={azureUserEmail}
                    onChange={(e) => setAzureUserEmail(e.target.value)}
                  />
                </Collapse>
                <Collapse in={!openAi} timeout="auto">
                  <TextField
                    fullWidth
                    id="private-link-service-id"
                    label="Private Link Service ID"
                    variant="outlined"
                    sx={{ mb: 4 }}
                    value={azureServiceId}
                    onChange={(e) => setAzureServiceId(e.target.value)}
                  />
                </Collapse>
              </>
            )}
            {location === 'network' && (
              <Box mt={2}>
                <TextField
                  fullWidth
                  id="real-subnets"
                  label="Real subnet CIDR lists"
                  variant="outlined"
                  sx={{ mb: 4 }}
                  value={realSubnets}
                  onChange={(e) => setRealSubnets(e.target.value)}
                />
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={enableAddressTranslation}
                        onChange={(e) => setEnableAddressTranslation(e.target.checked)}
                      />
                    }
                    label="Enable address translation"
                    sx={{ mb: 4 }}
                  />
                  {enableAddressTranslation && (
                    <TextField
                      fullWidth
                      id="virtual-subnet-pools"
                      label="Virtual subnet CIDR pool list"
                      variant="outlined"
                      sx={{ mb: 4 }}
                      value={virtualSubnetPools}
                      onChange={(e) => setVirtualSubnetPools(e.target.value)}
                    />
                  )}
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={enableBGP}
                        onChange={(e) => setEnableBGP(e.target.checked)}
                      />
                    }
                    label="Enable BGP"
                  />
                  {enableBGP && (
                    <>
                      <TextField
                        fullWidth
                        id="bgp-local-asn"
                        label="Bumblebee side ASN"
                        variant="outlined"
                        sx={{ mb: 4, mt: 4 }}
                        value={bgpLocalAsn}
                        onChange={(e) => setBgpLocalAsn(e.target.value)}
                      />
                      <TextField
                        fullWidth
                        id="bgp-peer-asn"
                        label="Customer side ASN"
                        variant="outlined"
                        sx={{ mb: 4 }}
                        value={bgpPeerAsn}
                        onChange={(e) => setBgpPeerAsn(e.target.value)}
                      />
                      <TextField
                        fullWidth
                        id="bgp-peer-ip"
                        label="Customer side BGP peer IP address"
                        variant="outlined"
                        sx={{ mb: 4 }}
                        value={bgpPeerIp}
                        onChange={(e) => setBgpPeerIp(e.target.value)}
                      />
                    </>
                  )}
                </Box>
                <Grid container spacing={4} mt={1}>
                  <Grid item xs={12} md={8} display="flex" alignItems="center">
                    {groupData && groupData?.data.length > 0 ? (
                      <FormControl fullWidth sx={{ mb: 4 }}>
                        <InputLabel id="service-nodes-label">Service Node Groups</InputLabel>
                        <Select
                          labelId="service-nodes-label"
                          id="service-nodes"
                          label="Service Node Groups"
                          value={selectedServiceNodeGroup}
                          onChange={(e) => setSelectedServiceNodeGroup(e.target.value)}
                        >
                          {groupData?.data.map(
                            ({
                              service_node_group_name,
                              service_node_group_id,
                            }: ServiceNodeGroup) => (
                              <MenuItem value={service_node_group_id} key={service_node_group_id}>
                                {service_node_group_name}
                              </MenuItem>
                            ),
                          )}
                        </Select>
                      </FormControl>
                    ) : (
                      <Box sx={{ textAlign: 'center', width: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                          No Service Node Groups found.
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Button
                      variant="text"
                      onClick={() => setShowNodeCreate(true)}
                      startIcon={<AddCircleOutlineOutlinedIcon fontSize="small" />}
                    >
                      New Group
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}
            <Box display="flex" justifyContent="space-between" sx={{ mt: 10 }}>
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                onClick={handleSubmit}
                disabled={isDisabled()}
              >
                Create
              </Button>
            </Box>
          </Box>
        </Box>
      </Slide>
      <Slide
        direction="left"
        in={showNodeCreate}
        container={containerRef.current}
        mountOnEnter
        unmountOnExit
      >
        <Box>
          <ServiceNodeCreate onCancel={handleCreateModalClose} showExisting={false} />
          <Divider sx={{ my: 4 }} />
          <Button variant="text" onClick={handleCreateModalClose} startIcon={<ArrowBackIcon />}>
            Return to Create App Service
          </Button>
        </Box>
      </Slide>
      {loadingModelOpen && <PageLoading content="Working on it..." />}
    </Paper>
  );
};
